import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, shareReplay } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { environment } from '../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import Keycloak from 'keycloak-js';

export interface TopicParts {
  organization: string;
  site: string;
}

export interface WsMessage {
  type: string;
  topic?: string;
  event?: string;
  payload?: any;
  message?: string;
  topic_parts?: TopicParts;
}

@Injectable({
  providedIn: 'root'
})
export class WsService {

  private socket?: WebSocketSubject<WsMessage>;
  private stream$?: Observable<WsMessage>;
  private internalSub?: Subscription;

  private closeCurrentSocket?: () => void;

  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private reconnectAttempt = 0;

  private readonly maxReconnectDelay = 30_000;

  private subscriptions = new Set<string>();

  private incomingMessagesSubject = new Subject<WsMessage>();
  readonly incomingMessages$ =
    this.incomingMessagesSubject.asObservable();

  private connectedSubject = new Subject<void>();
  readonly connected$ =
    this.connectedSubject.asObservable();

  private disconnectedSubject = new Subject<CloseEvent>();
  readonly disconnected$ =
    this.disconnectedSubject.asObservable();

  constructor(
    private keycloak: Keycloak,
    private toastr: ToastrService
  ) {
    this.keycloak.onTokenExpired = () => {
      void this.handleTokenExpiration();
    };
  }

  async connect(): Promise<void> {
    console.debug('WS connect called');

    if (this.socket && !this.socket.closed) {
      return;
    }

    try {
      await this.keycloak.updateToken(30);
    } catch (err) {
      console.warn(
        'Unable to refresh Keycloak token',
        err
      );

      // Pas de logout ici.
      this.scheduleReconnect();
      return;
    }

    const token = this.keycloak.token;

    if (!token) {
      console.warn('No Keycloak token available');
      this.scheduleReconnect();
      return;
    }

    this.createSocket(token);
  }

  private createSocket(token: string): void {

    /*
     * Cette variable appartient uniquement à cette instance
     * précise de WebSocket.
     *
     * Cela évite le problème d'un booléen global qui peut
     * changer avant que le closeObserver soit exécuté.
     */
    let intentionalClose = false;

    const socket = webSocket<WsMessage>({
      url:
        `${environment.websocket}/?token=${encodeURIComponent(token)}`,

      openObserver: {
        next: () => {

          /*
           * Entre la création et l'ouverture effective,
           * une autre WebSocket peut avoir été créée.
           */
          if (this.socket !== socket) {
            console.debug(
              'Ignoring open from obsolete WebSocket'
            );

            intentionalClose = true;
            socket.complete();
            return;
          }

          console.debug('✅ WebSocket connected');

          this.reconnectAttempt = 0;

          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
          }

          this.connectedSubject.next();

          this.resubscribeAll();
        }
      },

      closeObserver: {
        next: (event: CloseEvent) => {

          console.debug(
            '❌ WebSocket closed',
            event.code,
            event.reason,
            {
              intentionalClose
            }
          );

          /*
           * Important :
           *
           * une ancienne WebSocket peut finir de se fermer
           * après qu'une nouvelle WebSocket soit déjà active.
           *
           * Dans ce cas, son closeObserver ne doit surtout
           * pas déclencher une nouvelle reconnexion.
           */
          if (this.socket !== socket) {
            console.debug(
              'Ignoring close from obsolete WebSocket'
            );
            return;
          }

          this.cleanupSocket(socket);

          this.disconnectedSubject.next(event);

          if (intentionalClose) {
            console.debug(
              'Intentional WebSocket close'
            );
            return;
          }

          /*
           * Convention applicative.
           *
           * Le backend peut utiliser 4401 lorsqu'il sait
           * explicitement que l'authentification est invalide.
           */
          if (event.code === 4401) {
            console.warn(
              'WebSocket authentication rejected'
            );

            void this.logout();
            return;
          }

          /*
           * Tous les autres cas :
           *
           * - pod Kubernetes supprimé
           * - rolling update
           * - ingress restart
           * - coupure réseau
           * - Wi-Fi
           * - code 1005 / 1006
           *
           * => tentative de reconnexion.
           */
          this.scheduleReconnect();
        }
      }
    });

    /*
     * On enregistre la nouvelle socket AVANT de s'y abonner.
     */
    this.socket = socket;

    this.stream$ = socket.asObservable().pipe(
      shareReplay({
        bufferSize: 1,
        refCount: false
      })
    );

    this.internalSub = this.stream$.subscribe({

      next: (msg) => {
        /*
         * Ne transmet que les messages provenant encore
         * de la socket courante.
         */
        if (this.socket === socket) {
          this.incomingMessagesSubject.next(msg);
        }
      },

      error: (err) => {
        console.warn(
          'WS internal error',
          err
        );

        /*
         * Pas de logout ici.
         *
         * Le closeObserver décidera de la suite.
         */
      },

      complete: () => {
        console.debug(
          'WS internal complete'
        );
      }
    });

    /*
     * Fonction de fermeture spécifique à CETTE socket.
     *
     * Le flag intentionalClose est donc capturé par closure.
     */
    this.closeCurrentSocket = () => {
      intentionalClose = true;

      if (!socket.closed) {
        socket.complete();
      }
    };
  }

  private scheduleReconnect(): void {

    /*
     * Empêche plusieurs timers concurrents.
     */
    if (this.reconnectTimer) {
      return;
    }

    /*
     * Exponential backoff :
     *
     * 1s
     * 2s
     * 4s
     * 8s
     * 16s
     * 30s
     * 30s...
     */
    const exponentialDelay = Math.min(
      1000 * Math.pow(
        2,
        this.reconnectAttempt
      ),
      this.maxReconnectDelay
    );

    /*
     * Jitter pour éviter que tous les clients
     * se reconnectent exactement en même temps
     * lors d'un rolling update.
     */
    const jitter = Math.random() * 1000;

    const delay =
      exponentialDelay + jitter;

    this.reconnectAttempt++;

    console.debug(
      `WS reconnect scheduled in ${Math.round(delay)} ms`
    );

    this.reconnectTimer =
      setTimeout(() => {

        this.reconnectTimer =
          undefined;

        void this.tryReconnect();

      }, delay);
  }

  private async tryReconnect(): Promise<void> {
    if (this.socket && !this.socket.closed) {
      return;
    }

    if (!navigator.onLine) {
      console.debug('Browser offline, delaying WS reconnect');
      this.scheduleReconnect();
      return;
    }

    /*
     * Si le token est encore valide pendant au moins 30 secondes,
     * inutile de contacter Keycloak.
     */
    if (!this.keycloak.isTokenExpired(30)) {
      const token = this.keycloak.token;

      if (!token) {
        console.warn('No Keycloak token available');
        await this.logout();
        return;
      }

      console.debug('Trying WebSocket reconnect with current token');
      this.createSocket(token);
      return;
    }

    /*
     * Le token est expiré ou proche de l'expiration :
     * on demande à Keycloak de le renouveler.
     */
    try {
      await this.keycloak.updateToken(30);
    } catch (err) {
      console.warn(
        'Keycloak session can no longer be refreshed',
        err
      );

      await this.logout();
      return;
    }

    const token = this.keycloak.token;

    if (!token) {
      console.warn('No Keycloak token after refresh');
      await this.logout();
      return;
    }

    if (this.socket && !this.socket.closed) {
      return;
    }

    console.debug('Trying WebSocket reconnect with refreshed token');
    this.createSocket(token);
  }

  private async handleTokenExpiration(): Promise<void> {
    console.debug('Keycloak token expired');

    try {
      const refreshed = await this.keycloak.updateToken(30);

      if (refreshed) {
        console.debug('Keycloak token refreshed');
      }
    } catch (err) {
      console.warn(
        'Keycloak session can no longer be refreshed',
        err
      );

      await this.logout();
    }
  }

  reconnect(): void {

    console.debug(
      'Manual WebSocket reconnect'
    );

    /*
     * Cette méthode peut rester publique si tu veux
     * pouvoir forcer une reconnexion depuis ailleurs.
     */

    this.closeSocketOnly();

    this.reconnectAttempt = 0;

    void this.connect();
  }

  messages$(): Observable<WsMessage> {

    if (!this.stream$) {
      throw new Error(
        'WebSocket not connected'
      );
    }

    return this.stream$;
  }

  private static topicKey(
    topicParts: TopicParts
  ): string {
    return JSON.stringify(
      topicParts
    );
  }

  private static parseTopicKey(
    key: string
  ): TopicParts {
    return JSON.parse(key) as TopicParts;
  }

  subscribeTopic(
    topicParts: TopicParts
  ): void {

    const key =
      WsService.topicKey(topicParts);

    /*
     * La souscription est déjà enregistrée.
     */
    if (
      this.subscriptions.has(key)
    ) {
      return;
    }

    /*
     * On garde toujours la souscription en mémoire,
     * même si la WebSocket est momentanément indisponible.
     */
    this.subscriptions.add(key);

    if (
      !this.socket ||
      this.socket.closed
    ) {
      console.debug(
        'WebSocket disconnected, subscription retained',
        topicParts
      );
      return;
    }

    console.debug(
      'Subscribe WebSocket topic',
      topicParts
    );

    this.sendSubscription(
      'subscribe',
      topicParts
    );
  }

  unsubscribeTopic(
    topicParts: TopicParts
  ): void {

    const key =
      WsService.topicKey(topicParts);

    if (
      !this.subscriptions.has(key)
    ) {
      return;
    }

    this.subscriptions.delete(key);

    if (
      !this.socket ||
      this.socket.closed
    ) {
      return;
    }

    console.debug(
      'Unsubscribe WebSocket topic',
      topicParts
    );

    this.sendSubscription(
      'unsubscribe',
      topicParts
    );
  }

  unsubscribeAll(): void {

    const currentTopics =
      Array.from(
        this.subscriptions
      ).map(
        key =>
          WsService.parseTopicKey(key)
      );

    /*
     * D'abord vider l'état local.
     */
    this.subscriptions.clear();

    /*
     * Si la socket est coupée, il n'y a rien
     * à envoyer au serveur.
     */
    if (
      !this.socket ||
      this.socket.closed
    ) {
      return;
    }

    for (
      const topicParts
      of currentTopics
      ) {
      this.sendSubscription(
        'unsubscribe',
        topicParts
      );
    }
  }

  private resubscribeAll(): void {

    if (
      !this.socket ||
      this.socket.closed
    ) {
      return;
    }

    for (
      const key
      of this.subscriptions
      ) {

      const topicParts =
        WsService.parseTopicKey(key);

      console.debug(
        'Resubscribe WebSocket topic',
        topicParts
      );

      this.sendSubscription(
        'subscribe',
        topicParts
      );
    }
  }

  private sendSubscription(
    action:
      | 'subscribe'
      | 'unsubscribe',
    topicParts: TopicParts
  ): void {

    if (
      !this.socket ||
      this.socket.closed
    ) {
      return;
    }

    this.socket.next({
      action,
      topic_parts: topicParts
    } as any);
  }

  eventsByTopic$(
    topic: string
  ): Observable<WsMessage> {

    return this.incomingMessages$
      .pipe(
        filter(
          (msg) =>
            msg.type === 'event' &&
            msg.topic === topic
        )
      );
  }

  close(
    clearSubscriptions = true
  ): void {

    console.debug(
      'WebSocket close called'
    );

    /*
     * Logout / fermeture volontaire de l'application :
     * on peut supprimer les topics.
     *
     * Pour une reconnexion interne, on utilise
     * closeSocketOnly() directement.
     */
    if (clearSubscriptions) {
      this.subscriptions.clear();
    }

    this.closeSocketOnly();
  }

  private closeSocketOnly(): void {

    /*
     * Annule une reconnexion qui aurait été programmée.
     */
    if (this.reconnectTimer) {
      clearTimeout(
        this.reconnectTimer
      );

      this.reconnectTimer =
        undefined;
    }

    /*
     * On garde la fonction locale avant
     * d'effacer les références.
     */
    const closeSocket =
      this.closeCurrentSocket;

    this.closeCurrentSocket =
      undefined;

    /*
     * Très important :
     *
     * on retire la socket courante AVANT complete().
     *
     * Ainsi, lorsque son closeObserver arrivera,
     * il verra :
     *
     * this.socket !== socket
     *
     * et ignorera proprement l'événement.
     */
    this.socket = undefined;
    this.stream$ = undefined;

    this.internalSub?.unsubscribe();
    this.internalSub = undefined;

    closeSocket?.();
  }

  private cleanupSocket(
    socket: WebSocketSubject<WsMessage>
  ): void {

    /*
     * Une ancienne socket ne doit jamais nettoyer
     * l'état d'une nouvelle socket.
     */
    if (this.socket !== socket) {
      return;
    }

    this.internalSub?.unsubscribe();
    this.internalSub = undefined;

    this.socket = undefined;
    this.stream$ = undefined;
    this.closeCurrentSocket = undefined;
  }

  private async logout(): Promise<void> {

    console.debug(
      'Logging out after Keycloak authentication failure'
    );

    /*
     * On arrête toute activité WebSocket avant logout.
     *
     * clearSubscriptions=true par défaut.
     */
    this.close();

    try {
      await this.keycloak.logout();

    } catch (err) {
      console.error(
        'Logout failed',
        err
      );

      this.toastr.error(
        'Unable to logout'
      );
    }
  }
}
